import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import {
  VehicleModel,
  buildVehiclePath,
  buildDefaultVehiclePath,
  buildVehicleStylePath,
} from 'client/data/models/vehicle-v2';
import { HTTP_NOT_FOUND } from 'client/utils/http-status';
import { updateVehicle, buildContextVehicleFromMakeModelSubmodelYear } from 'client/actions/vehicle';
import { isPreProd, isUsed as isUsedPubState } from 'site-modules/shared/utils/publication-states';
import { getModelState } from 'client/data/luckdragon/model';

/**
 * A preloader that assists with updating/resolving multiple model paths so they are preloaded into redux,
 * while also validating that vehicle requested is valid.
 *
 * @see {@link ModelPreloader}
 *
 * @param {Object} store - The redux store where the model will be stored.
 * @param {Object} props
 * @param {Object} params.
 */
export function VehiclePreloader(store, props, { isSubpage = true, vehicleModel = VehicleModel } = {}) {
  const preloader = new ModelPreloader(store);

  const vehiclePath = props.params.year ? buildVehiclePath(props.params) : buildDefaultVehiclePath(props.params);

  preloader.resolve(vehiclePath, vehicleModel);

  if (props.params.styleId) {
    preloader.resolve(buildVehicleStylePath(props.params), vehicleModel);
  }

  /**
   * Marks a model path to be updated with the given value.
   *
   * @see {@link ModelPreloader.set}
   */
  this.set = function set(path, segment, value) {
    preloader.set(path, segment, value);
  };

  /**
   * Marks a model path to be resolved.
   *
   * @see {@link ModelPreloader.resolve}
   */
  this.resolve = function resolve(path, segment) {
    preloader.resolve(path, segment);
  };

  /**
   * Validate vehicle and throw error
   */
  this.validateVehicle = function validateVehicle(vehicle) {
    const preprod = vehicle && vehicle.pubStates && isPreProd(vehicle.pubStates);

    if (
      !vehicle ||
      !vehicle.make ||
      !vehicle.model ||
      !vehicle.year ||
      !vehicle.submodels ||
      !vehicle.pubStates ||
      (isSubpage && preprod)
    ) {
      const errorMsg =
        preprod && isSubpage
          ? `VehicleValidatorError - path:${vehiclePath} not supported on pre-production models`
          : `VehicleValidatorError - path:${vehiclePath} not found`; // similar message pattern to help with switching between NR queries
      const ex = new Error(errorMsg);
      ex.status = HTTP_NOT_FOUND;
      ex.redirectPageTo404 = true;
      throw ex;
    }
  };

  this.dispatch = function dispatch(modelState, vehicle) {
    const isPreProdVehicle = isPreProd(vehicle.pubStates);
    const isUsed = isUsedPubState(vehicle.pubStates);
    const vehicleForContext = buildContextVehicleFromMakeModelSubmodelYear(vehicle, { isUsed, isPreProdVehicle });
    store.dispatch(updateVehicle(vehicleForContext));
  };

  let isFirstLoad = true;

  /**
   * Preloads the model data into the store, but will 404 if the requested vehicle is invalid.
   *
   * @see {@link ModelPreloader.load}
   */
  this.load = async function load() {
    let errorFromLoad;
    const waitForAll = isFirstLoad;
    isFirstLoad = false;

    return preloader
      .load({ waitForAll })
      .catch(e => {
        errorFromLoad = e;
      })
      .then(modelStateRef => {
        // getModelState() clones state, expensive, no need if modelStateRef is defined (load was success)
        // if not defined (because load failed), we should still getModelState() so we can validate vehicle
        const modelState = modelStateRef || getModelState(store.getState());
        const vehicle = modelState.get(vehiclePath, vehicleModel);
        this.validateVehicle(vehicle); // may throw, for 404 to work we need to make sure this is what's re-thrown instead of the original errorFromLoad
        this.dispatch(modelState, vehicle);
        // return modelStateRef because it will have changes from above dispatch vs modelState=getModelState(store.getState()) would not have.
        // modelStateRef will also get changes from future dispatches from page preload, if any, in case any existing code relies on this.
        // if modelStateRef is undefined, this means there was errorFromLoad, doesn't matter what's returned cause next then() is gonna throw.
        return modelStateRef;
      })
      .catch(errorFromValidate => {
        if (errorFromLoad) {
          // eslint-disable-next-line no-param-reassign
          errorFromValidate.message = `${
            errorFromValidate.message
          } (note: there was another error silenced by this error. For reference it is made available in the cause field)`;
          // eslint-disable-next-line no-param-reassign
          errorFromValidate.cause = errorFromLoad;
        }
        throw errorFromValidate;
      })
      .then(modelState => {
        if (errorFromLoad) {
          throw errorFromLoad;
        }
        return modelState;
      });
  };
}
